import {_utils, viewportChecker} from "../../utils/_utils.js";
import {_libs} from "../../utils/_libs.js";
import {_state} from "../../state/state.js";
import {watchPageHeight} from "../../utils/scroll-refresh.js";

/**
 * "From reservation to departure" steps (TZ, section 7).
 * - Desktop: the step area is position: sticky (vertically centered) inside a taller stage (CSS);
 *   scroll progress picks the active step — text, image and counter switch in sync; reverse scroll goes back.
 * - Mobile/tablet: prev/next round buttons (as in the mobile design), no scroll effect.
 * Transition (CSS): new photo slides in from the right, previous drifts left; text fades + moves up.
 */
export const hpSteps = () => {
    return new Promise((resolve) => {
        _utils.initSection('.hp-steps', (section) => init(section));
        watchPageHeight();
        resolve();
    });
};

const init = (section) => {
    const stage = section.querySelector('.hp-steps__stage');
    const sticky = section.querySelector('.hp-steps__sticky');
    const items = [...section.querySelectorAll('.hp-steps__item')];
    const images = [...section.querySelectorAll('.hp-steps__image')];
    const counter = section.querySelector('.hp-steps__counter-current');
    const prevBtn = section.querySelector('.hp-steps__nav-btn--prev');
    const nextBtn = section.querySelector('.hp-steps__nav-btn--next');
    if (!stage || !sticky || items.length < 2) {
        if (nextBtn) nextBtn.disabled = true;
        return;
    }

    let current = 0;
    let tl = null;

    const setActive = (index) => {
        if (index === current || index < 0 || index >= items.length) return;
        const previous = current;
        current = index;

        items.forEach((item, i) => {
            item.classList.toggle('is-active', i === index);
            item.classList.toggle('is-leaving', i === previous);
            item.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        images.forEach((image, i) => {
            image.classList.toggle('is-active', i === index);
            image.classList.toggle('is-prev', i < index);
            image.classList.toggle('is-next', i > index);
        });

        if (counter) counter.textContent = String(index + 1).padStart(2, '0');
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === items.length - 1;
    };

    const stickyTop = () => parseFloat(getComputedStyle(sticky).top) || 0;

    const createTl = () => {
        if (tl !== null) {
            tl.scrollTrigger && tl.scrollTrigger.kill(true);
            tl.kill();
            tl = null;
        }

        if (_state.isDesktop) {
            // from the moment the step area sticks until it un-sticks (stage bottom = sticky bottom)
            tl = _libs._gsap({
                trigger: stage,
                start: () => `top ${stickyTop()}px`,
                end: () => `bottom ${stickyTop() + sticky.offsetHeight}px`,
                invalidateOnRefresh: true,
                //markers: true,
                onUpdate: (self) => {
                    setActive(Math.min(items.length - 1, Math.floor(self.progress * items.length)));
                },
            });
        }
    };

    createTl();

    // mobile / tablet: arrows
    prevBtn?.addEventListener('click', () => setActive(current - 1));
    nextBtn?.addEventListener('click', () => setActive(current + 1));

    // Rebuild only when crossing the desktop breakpoint. Other size changes (incl. the theme's "resizePage" on
    // any page-height change) are handled by ScrollTrigger.refresh() inside _libs._gsap — re-creating pins here
    // mid-scroll would make the page jump.
    let wasDesktop = _state.isDesktop;
    _utils.rebuildComponentsOnResize(() => {
        viewportChecker.all();
        if (_state.isDesktop === wasDesktop) return;
        wasDesktop = _state.isDesktop;
        createTl();
    });
};
